import { useCallback, useState } from "react";

/**
 * Custom hook for managing scroll positions with localStorage persistence
 * @param {string} storageKey - The key to use for localStorage
 * @param {number} defaultValue - Default scroll position value
 * @param {string} containerSelector - CSS selector for the scrollable container (optional)
 * @returns {Object} - { scrollPosition, setScrollPosition, saveScrollPosition }
 */
export const useScrollPosition = (
  storageKey,
  defaultValue = 0,
  containerSelector = null,
) => {
  const [scrollPosition, setScrollPosition] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? parseInt(saved, 10) : defaultValue;
    } catch (error) {
      console.warn(`Failed to load scroll position for ${storageKey}:`, error);
      return defaultValue;
    }
  });

  const saveScrollPosition = useCallback(
    (position) => {
      try {
        localStorage.setItem(storageKey, position.toString());
        setScrollPosition(position);
      } catch (error) {
        console.warn(
          `Failed to save scroll position for ${storageKey}:`,
          error,
        );
      }
    },
    [storageKey],
  );

  const handleScroll = useCallback(() => {
    const position = containerSelector
      ? document.querySelector(containerSelector)?.scrollTop || 0
      : window.pageYOffset;
    saveScrollPosition(position);
  }, [containerSelector, saveScrollPosition]);

  const restoreScrollPosition = useCallback(() => {
    const timer = setTimeout(() => {
      if (containerSelector) {
        const container = document.querySelector(containerSelector);
        if (container) {
          container.scrollTop = scrollPosition;
        }
      } else {
        window.scrollTo(0, scrollPosition);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [containerSelector, scrollPosition]);

  return {
    scrollPosition,
    setScrollPosition: saveScrollPosition,
    saveScrollPosition,
    handleScroll,
    restoreScrollPosition,
  };
};

/**
 * Custom hook for managing tab state with localStorage persistence
 * @param {string} storageKey - The key to use for localStorage
 * @param {string} defaultValue - Default tab value
 * @returns {Array} - [activeTab, setActiveTab]
 */
export const useTabState = (storageKey, defaultValue) => {
  const [activeTab, setActiveTab] = useState(() => {
    try {
      return localStorage.getItem(storageKey) || defaultValue;
    } catch (error) {
      console.warn(`Failed to load tab state for ${storageKey}:`, error);
      return defaultValue;
    }
  });

  const handleTabChange = useCallback(
    (newTab) => {
      try {
        localStorage.setItem(storageKey, newTab);
        setActiveTab(newTab);
      } catch (error) {
        console.warn(`Failed to save tab state for ${storageKey}:`, error);
        setActiveTab(newTab);
      }
    },
    [storageKey],
  );

  return [activeTab, handleTabChange];
};

/**
 * Custom hook for managing filter state with localStorage persistence
 * @param {string} storageKey - The key to use for localStorage
 * @param {string} defaultValue - Default filter value
 * @returns {Array} - [filter, setFilter]
 */
export const useFilterState = (storageKey, defaultValue = "") => {
  const [filter, setFilter] = useState(() => {
    try {
      return localStorage.getItem(storageKey) || defaultValue;
    } catch (error) {
      console.warn(`Failed to load filter state for ${storageKey}:`, error);
      return defaultValue;
    }
  });

  const handleFilterChange = useCallback(
    (newFilter) => {
      try {
        localStorage.setItem(storageKey, newFilter || "");
        setFilter(newFilter);
      } catch (error) {
        console.warn(`Failed to save filter state for ${storageKey}:`, error);
        setFilter(newFilter);
      }
    },
    [storageKey],
  );

  return [filter, handleFilterChange];
};

/**
 * Custom hook for managing sorting state with localStorage persistence
 * @param {string} storageKey - The key to use for localStorage
 * @param {Array} defaultValue - Default sorting value
 * @returns {Array} - [sorting, setSorting]
 */
export const useSortingState = (storageKey, defaultValue) => {
  const [sorting, setSorting] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : defaultValue;
    } catch (error) {
      console.warn(`Failed to load sorting state for ${storageKey}:`, error);
      return defaultValue;
    }
  });

  const handleSortingChange = useCallback(
    (newSorting) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(newSorting));
        setSorting(newSorting);
      } catch (error) {
        console.warn(`Failed to save sorting state for ${storageKey}:`, error);
        setSorting(newSorting);
      }
    },
    [storageKey],
  );

  return [sorting, handleSortingChange];
};
